import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Alert,
  Spinner,
  Form,
  InputGroup,
} from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { authFetch } from "../utils/authFetch";
import { toast } from "react-toastify";

function formatDateRo(val) {
  if (!val) return "";
  const s = String(val);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
}

export default function RecipeList() {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  const requestId = useRef(0);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchList = async () => {
    const request = ++requestId.current;
    setLoading(true);
    setError("");
    try {
      const skip = currentPage * itemsPerPage;
      const usp = new URLSearchParams();
      if (searchTerm.trim()) {
        usp.set("q", searchTerm.trim());
      }
      usp.set("skip", String(skip));
      usp.set("limit", String(itemsPerPage));

      if (showActiveOnly) usp.set("active_only", "true");
      const data = await authFetch(`/bxreteta/page?${usp.toString()}`);
      if (request !== requestId.current) return;
      if (!Array.isArray(data?.items) || !Number.isInteger(data.total)) {
        throw new Error("Lista de rețete nu are un răspuns paginat valid.");
      }
      setTotalCount(data.total);
      if (skip >= data.total && currentPage > 0) {
        setCurrentPage(Math.max(0, Math.ceil(data.total / itemsPerPage) - 1));
        setItems([]);
        return;
      }
      setItems(data.items);
    } catch (e) {
      if (request !== requestId.current) return;
      setItems([]); setTotalCount(0);
      setError(String(e.message || e));
    } finally {
      if (request === requestId.current) setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Sigur doriți să ștergeți această rețetă cu toate materialele ei?",
      )
    ) {
      return;
    }
    try {
      await authFetch(`/bxreteta/${id}`, { method: "DELETE" });
      toast.success("Rețeta a fost ștearsă.");
      fetchList();
    } catch (e) {
      toast.error(`Eroare la ștergere: ${e.message || e}`);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => { clearTimeout(t); requestId.current += 1; };
  }, [currentPage, itemsPerPage, searchTerm, showActiveOnly]);

  const pageCount = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const from = totalCount ? currentPage * itemsPerPage + 1 : 0;
  const to = Math.min(totalCount, currentPage * itemsPerPage + items.length);

  return (
    <Container className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h3 className="mb-0">Retete</h3>
        </Col>
        <Col xs="auto">
          <Button as={Link} to="/retete/new" variant="success">
            + Rețetă nouă
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Cauta după produs finit, cod sau denumire reteta..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
            />
          </InputGroup>
        </Col>

        <Col md={3} className="d-flex align-items-center">
          <Form.Check
            type="checkbox"
            label="Doar rețete active"
            checked={showActiveOnly}
            onChange={(e) => { setShowActiveOnly(e.target.checked); setCurrentPage(0); }}
          />
        </Col>

        <Col
          md={3}
          className="d-flex justify-content-end align-items-center gap-2"
        >
          <Form.Select
            size="sm"
            style={{ width: 110 }}
            value={itemsPerPage}
            aria-label="Rețete pe pagină"
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(0); }}
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}/pag.
              </option>
            ))}
          </Form.Select>
          <Button variant="outline-primary" onClick={fetchList}>
            Caută
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Table striped bordered hover size="sm" className="align-middle">
            <thead className="table-dark">
              <tr>
                <th className="text-center" style={{ width: "80px" }}>
                  Activă
                </th>
                <th>Cod</th>
                <th>Denumire Rețetă</th>
                <th>Produs Finit</th>
                <th>Valabilitate Start</th>
                <th>Valabilitate Stop</th>
                <th style={{ width: 250 }} />
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-3">
                    Nicio rețetă găsită.
                  </td>
                </tr>
              ) : null}

              {items.map((it) => (
                <tr key={it.id}>
                  <td className="text-center fs-5">
                    {it.activa === 1 ? "⭐" : ""}
                  </td>
                  <td>
                    <strong>{it.cod_reteta}</strong>
                  </td>
                  <td>{it.denumire}</td>
                  <td>
                    <strong>{it.produs_finit_cod_str || "-"}</strong> —{" "}
                    {it.produs_finit_denumire || "-"}
                  </td>
                  <td>{formatDateRo(it.data_start) || "-"}</td>
                  <td>{formatDateRo(it.data_stop) || "-"}</td>
                  <td className="text-end">
                    <Button
                      as={Link}
                      size="sm"
                      variant="primary"
                      to={`/retete/${it.id}`}
                      className="me-2"
                    >
                      Editează
                    </Button>
                    <Button as={Link} size="sm" variant="outline-primary"
                      className="me-2" to={`/retete/new?copy_from=${it.id}`}>Copiază</Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDelete(it.id)}
                    >
                      Șterge
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="text-muted small mb-2">Afișare {from}–{to} din {totalCount} rețete</div>
          <ReactPaginate
            previousLabel={"← Înapoi"}
            nextLabel={"Înainte →"}
            pageCount={pageCount}
            forcePage={currentPage}
            onPageChange={(event) => setCurrentPage(event.selected)}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            activeClassName={"active"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            renderOnZeroPageCount={null}
          />
        </>
      )}
    </Container>
  );
}
